import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { Lightbulb, X, SquarePen, Maximize2, Minimize2 } from "lucide-react";
import ButtonWithConfirm from "components/button/buttonwithconfirm";
import HeaderComponent from "components/card/HeaderComponent";

import { doAgentChat, doLoadChatHistory, doNewChat, doRetryAgentChat } from "../../redux/copilot/copilotState";
import EliConversation from "./EliConversation";
import { ReactComponent as EliHead } from 'assets/svg/Eli_Chatbot.svg';

// Remembered across sessions so a user who works in the big panel is not made to
// re-expand it every single time. Same plain localStorage approach the theme
// toggle uses (components/navbar/index.jsx).
const ELI_PANEL_EXPANDED_KEY = "eliPanelExpanded";

export const ChatEli = ({ doChatMessage, doLoadHistory = () => {}, doNewChat = () => {}, doRetry = () => {}, historyLoaded = false, responseData, requesting, size }) => {
    const [showChatEli, setShowChatEli] = useState(false);
    const [expanded, setExpanded] = useState(() => localStorage.getItem(ELI_PANEL_EXPANDED_KEY) === "true");
    const launcherRef = useRef(null);
    const inputRef = useRef(null);

    const toggleExpanded = () => {
        setExpanded((prev) => {
            const next = !prev;
            localStorage.setItem(ELI_PANEL_EXPANDED_KEY, String(next));
            return next;
        });
    };

    const openPanel = () => setShowChatEli(true);
    const closePanel = () => {
        setShowChatEli(false);
        launcherRef.current?.focus();
    };

    useEffect(() => {
        if (showChatEli) {
            inputRef.current?.focus();
        }
    }, [showChatEli]);

    // The "already fetched" flag lives in redux, not in a ref here. This component
    // UNMOUNTS on the Eli page (the bubble is hidden there), so a ref would reset on
    // remount and refetch the newest conversation over whatever the user had opened.
    //
    // Deps are deliberately [showChatEli, historyLoaded] and NOT `requesting`. A
    // failed load leaves historyLoaded false, and because neither dep changed the
    // effect does not fire again, so a persistent failure cannot spin. It retries on
    // the next open, which is what the user would expect.
    useEffect(() => {
        if (showChatEli && !historyLoaded) {
            doLoadHistory();
        }
    }, [showChatEli, historyLoaded]);

    const launcherHeight = size === "small" ? "44px" : size === "large" ? "84px" : "64px";

    // Only ask when there is something to lose. The panel opens holding just the
    // greeting, and a dialog over an untouched chat is a nag. "The user has said at
    // least one thing" is the precise version of "there is a conversation here".
    const hasConversation = !!responseData?.some((chat) => chat.type === "user");

    return (
        <>
            <div className="fixed bottom-[20px] right-[10px] z-[1000]">
                <button
                    type="button"
                    ref={launcherRef}
                    data-testid="eli-launcher"
                    aria-label={showChatEli ? "Close Eli chat" : "Open Eli chat"}
                    aria-expanded={showChatEli}
                    onClick={() => (showChatEli ? closePanel() : openPanel())}
                    className="flex items-center justify-center cursor-pointer p-2 rounded-full bg-primary-2 min-w-[44px] min-h-[44px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                    <EliHead style={{ color: "#fff", width: 'auto', height: launcherHeight }} />
                </button>
            </div>

            {showChatEli && (
                <div
                    data-testid="eli-chat-panel"
                    role="dialog"
                    aria-modal="false"
                    aria-label="Eli chat"
                    onKeyDown={(e) => {
                        // A confirm dialog renders in a portal, and antd does not move
                        // focus into it synchronously, so the Escape keydown's real
                        // target is still the trigger button, a normal descendant of
                        // this very panel div (which also carries role="dialog"). That
                        // means e.target.closest('[role="dialog"]') always matches this
                        // panel itself, on every keypress, dialog open or not, and
                        // cannot tell the two apart. Measured directly: checking for
                        // the antd modal's wrapper anywhere in the document, rather
                        // than in e.target's ancestry, is what actually distinguishes
                        // "a confirm dialog is open" from "no dialog is open", and does
                        // not swallow Escape when nothing is open.
                        //
                        // Known accepted limitation: the very first Escape pressed right
                        // after the dialog opens is a no-op, because focus has not yet
                        // moved into the dialog, so antd never sees that keypress either.
                        // A second Escape works normally. That is the trade for fixing
                        // this without touching the shared confirm component.
                        if (e.key === "Escape" && !document.querySelector(".ant-modal-wrap")) closePanel();
                    }}
                    className={`fixed bottom-[90px] right-[30px] w-[92vw] z-[1000] rounded-md shadow-2xl bg-white transition-[max-width,opacity] duration-200 motion-reduce:transition-none ${expanded ? "max-w-none xl:max-w-[calc(100vw_-_255px_-_4rem)]" : "max-w-[400px]"}`}
                >
                    <div className="overflow-y-auto rounded-md">
                        <HeaderComponent
                            title="Eli"
                            titleClass="text-white font-bold"
                            description="Your AI assistant"
                            descriptionClass="text-white"
                            bgcolor="!p-3 bg-gradient-to-br from-emerald-500 via-green-600 to-purple-600"
                            icon={<Lightbulb />}
                            colorTheme="text-white rounded-t-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 duration-300"
                        >
                            <button
                                type="button"
                                data-testid="eli-expand"
                                aria-label={expanded ? "Collapse chat panel" : "Expand chat panel"}
                                aria-pressed={expanded}
                                title={expanded ? "Collapse" : "Expand"}
                                onClick={toggleExpanded}
                                className="absolute top-2 right-[112px] text-white p-1 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                            >
                                {expanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                            </button>
                            {hasConversation ? (
                                <ButtonWithConfirm
                                    type="button"
                                    data-testid="eli-new-chat"
                                    aria-label="Start new chat"
                                    title="New chat"
                                    confirmHandler={() => doNewChat()}
                                    confirmationTitle="Start a new chat?"
                                    confirmationMessage="This conversation stays in your history and you can reopen it any time."
                                    confirmLabel="New chat"
                                    cancelLabel="Cancel"
                                    className="absolute top-2 right-[60px] text-white p-1 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white hover:shadow-none"
                                >
                                    <SquarePen className="w-5 h-5" />
                                </ButtonWithConfirm>
                            ) : (
                                <button
                                    type="button"
                                    data-testid="eli-new-chat"
                                    aria-label="Start new chat"
                                    title="New chat"
                                    onClick={() => doNewChat()}
                                    className="absolute top-2 right-[60px] text-white p-1 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                                >
                                    <SquarePen className="w-5 h-5" />
                                </button>
                            )}
                            <button
                                type="button"
                                data-testid="eli-close"
                                aria-label="Close Eli chat"
                                onClick={closePanel}
                                className="absolute top-2 right-2 text-white p-1 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <EliConversation
                                responseData={responseData}
                                requesting={requesting}
                                onSend={(text) => doChatMessage({ field: 'chat', message: text })}
                                onRetry={doRetry}
                                inputRef={inputRef}
                                messagesClassName={expanded ? "max-h-[calc(100vh_-_260px)]" : "max-h-[360px]"}
                            />
                        </HeaderComponent>
                    </div>
                </div>
            )}
        </>
    );
};

const mapStateToProps = (state) => ({
    responseData: state.copilot.chatData?.results,
    requesting: state.copilot.chatData?.requesting,
    success: state.copilot.chatData?.succcess,
    error: state.copilot.chatData?.error,
    historyLoaded: state.copilot.chatData.historyLoaded,
});

const mapDispatchToProps = (dispatch) => ({
    doChatMessage: (data) => dispatch(doAgentChat(data)),
    doLoadHistory: () => dispatch(doLoadChatHistory()),
    doNewChat: () => dispatch(doNewChat()),
    doRetry: (data, errorId) => dispatch(doRetryAgentChat(data, errorId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatEli);
